var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useContext } from 'react';
import ButtonComponent from './ButtonComponent';
import '../styles/shades.scss';
import globalStore from '../store';
import { DataContext } from '../player/DataContext';
import { selectTool } from '../helper/helper';
import customEventHandler from '../helper/customEvents';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
var Shades = function (props) {
    var _a, _b, _c, _d, _e, _f;
    var _g = props.id, id = _g === void 0 ? 'shades' : _g, type = props.type, _h = props.data, _j = _h.position, x = _j.x, y = _j.y, _k = _j.z, z = _k === void 0 ? 0 : _k, _l = _h.dimensions, width = _l.width, height = _l.height, revealed = _h.revealed, lock = _h.lock, onChange = props.onChange;
    var isPositionLocked = (_a = lock === null || lock === void 0 ? void 0 : lock.positioning) !== null && _a !== void 0 ? _a : false;
    var isEditLocked = (_b = lock === null || lock === void 0 ? void 0 : lock.editing) !== null && _b !== void 0 ? _b : false;
    var btnConfig = globalStore.useStoreState(function (store) { return store.jsonDataStore; }).btnConfig;
    var zoomLevel = globalStore.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    var _m = useContext(DataContext), selectedTool = _m.selectedTool, setSelectedTool = _m.setSelectedTool, setShades = _m.setShades, shades = _m.shades, setContextMenu = _m.setContextMenu;
    var _o = useState({ x: x, y: y, z: z }), pos = _o[0], setPos = _o[1];
    var _p = useState({ width: width, height: height }), size = _p[0], setSize = _p[1];
    var _q = useState(false), isDragging = _q[0], setIsDragging = _q[1];
    var _r = useState(false), isResizing = _r[0], setIsResizing = _r[1];
    var _s = useState(revealed), revealedData = _s[0], setRevealedData = _s[1];
    var _t = useState(false), triggerOnChange = _t[0], setTriggerOnChange = _t[1];
    var _u = useState(false), isFocused = _u[0], setIsFocused = _u[1];
    var startPosition = useRef({ x: 0, y: 0, width: 0, height: 0, angle: 0 });
    var oldDataRef = useRef(null);
    var containerRef = useRef(null);
    var localObj = {
        selectionType: "shades_".concat(id),
    };
    // Refs so the multi-select drag start/end (leader) and follower listener
    // (below) always read current values without going stale between renders.
    var posRef = useRef(pos);
    posRef.current = pos;
    var sizeRef = useRef(size);
    sizeRef.current = size;
    var revealedDataRef = useRef(revealedData);
    revealedDataRef.current = revealedData;
    var selectedToolRef = useRef(selectedTool);
    selectedToolRef.current = selectedTool;
    // Captured at drag start (leader side) so multiSelectMove can broadcast a
    // delta from THIS shade's own start position, not an absolute position.
    var multiDragStartPosRef = useRef({ x: 0, y: 0 });
    var _v = useMultiSelectDragSource(localObj.selectionType, selectedTool), startMultiDrag = _v.startMultiDrag, moveMultiDrag = _v.moveMultiDrag, endMultiDrag = _v.endMultiDrag;
    // Extract shade index from id (e.g. "id_123_2" → 2)
    var splittedId = localObj.selectionType.split('_');
    var shadeIndex = +splittedId[splittedId.length - 1];
    useEffect(function () {
        setPos({ x: x, y: y, z: z });
    }, [x, y, z]);
    useEffect(function () {
        setSize({ width: width, height: height });
    }, [width, height]);
    useEffect(function () {
        setRevealedData(revealed);
    }, [revealed]);
    useEffect(function () {
        if (triggerOnChange) {
            onChange &&
                onChange(id, 'mouseUp', {
                    position: __assign({}, pos),
                    dimensions: size,
                    revealed: revealedData,
                });
            // Save state for undo/redo
            if (oldDataRef.current) {
                var entry = {
                    type: 'shades',
                    id: String(shadeIndex),
                    propertyName: 'completeData',
                    old: JSON.stringify({
                        data: {
                            position: oldDataRef.current.position,
                            dimensions: oldDataRef.current.size,
                            revealed: oldDataRef.current.revealed,
                        },
                    }),
                    new: JSON.stringify({
                        data: {
                            position: __assign({}, pos),
                            dimensions: __assign({}, size),
                            revealed: revealedData,
                        },
                    }),
                };
                if (!registerMultiSelectSaveEntries(localObj.selectionType, [entry])) {
                    saveState(entry);
                }
                oldDataRef.current = null;
                customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
            }
            setTriggerOnChange(false);
        }
    }, [triggerOnChange]);
    useEffect(function () {
        if (isDragging || isResizing) {
            oldDataRef.current = { position: __assign({}, pos), size: __assign({}, size), revealed: revealedData };
            onChange &&
                onChange(id, 'mouseDown', {
                    position: __assign({}, pos),
                    dimensions: __assign({}, size),
                    revealed: revealedData,
                });
        }
    }, [isDragging, isResizing]);
    var updateShadeData = function (shadePosition, shadeSize, shadeRevealedData) {
        var splittedId = localObj.selectionType.split('_');
        var _shadeIndex = +splittedId[splittedId.length - 1];
        setShades(function (prev) {
            var newState = __spreadArray([], prev, true);
            newState[_shadeIndex] = __assign(__assign({}, newState[_shadeIndex]), { data: {
                    position: __assign({}, shadePosition),
                    dimensions: __assign({}, shadeSize),
                    revealed: shadeRevealedData,
                } });
            return newState;
        });
    };
    /** DRAG */
    var startDrag = function (e, clientX, clientY) {
        if (isFocused && !isPositionLocked) {
            setIsDragging(true);
            startPosition.current = __assign(__assign({}, startPosition.current), { x: clientX / zoomLevel - pos.x, y: clientY / zoomLevel - pos.y });
            multiDragStartPosRef.current = { x: pos.x, y: pos.y };
            // eslint-disable-next-line no-console
            console.log('[Shades debug] startDrag (leader)', {
                id: localObj.selectionType,
                selectedTool: selectedTool,
                willBroadcast: selectedTool.length > 1 && selectedTool.includes(localObj.selectionType),
            });
            startMultiDrag();
        }
        setSelectedTool(selectTool(e, localObj.selectionType, selectedTool, false));
    };
    var handleMouseDownDrag = function (e) {
        e.stopPropagation();
        if (e.target.id === 'shade')
            startDrag(e, e.clientX, e.clientY);
    };
    var handleTouchStartDrag = function (e) {
        // Prevent default touch behavior to avoid page refresh/scrolling on iPad
        e.preventDefault();
        var touch = e.touches[0];
        if (e.target.id === 'shade')
            startDrag(e, touch.clientX, touch.clientY);
    };
    /** RESIZE */
    var startResize = function (clientX, clientY) {
        if (isPositionLocked)
            return;
        setIsResizing(true);
        startPosition.current = __assign(__assign({}, startPosition.current), { x: clientX, y: clientY, width: size.width, height: size.height });
    };
    var handleMouseDownResize = function (e) {
        e.stopPropagation();
        startResize(e.clientX, e.clientY);
    };
    var handleTouchStartResize = function (e) {
        // Prevent default touch behavior to avoid page refresh/scrolling on iPad
        e.preventDefault();
        var touch = e.touches[0];
        e.stopPropagation();
        startResize(touch.clientX, touch.clientY);
    };
    /** MOVE / RESIZE  */
    useEffect(function () {
        /** handle mouse move and resize */
        var handleMove = function (clientX, clientY) {
            if (isDragging) {
                var _a = startPosition.current, x_1 = _a.x, y_1 = _a.y;
                var dx = clientX / zoomLevel - x_1;
                var dy = clientY / zoomLevel - y_1;
                var _setPos = __assign(__assign({}, pos), { x: dx, y: dy });
                setPos(__assign({}, _setPos));
                // eslint-disable-next-line no-console
                console.log('[Shades debug] handleMove (leader) moveMultiDrag', {
                    id: localObj.selectionType,
                    deltaX: _setPos.x - multiDragStartPosRef.current.x,
                    deltaY: _setPos.y - multiDragStartPosRef.current.y,
                });
                moveMultiDrag(_setPos.x - multiDragStartPosRef.current.x, _setPos.y - multiDragStartPosRef.current.y);
                updateShadeData(__assign({}, _setPos), size, revealedData);
            }
            else if (isResizing) {
                var _b = startPosition.current, x_2 = _b.x, y_2 = _b.y, width_1 = _b.width, height_1 = _b.height;
                var dx = (clientX - x_2) / zoomLevel;
                var dy = (clientY - y_2) / zoomLevel;
                var _setSize = {
                    width: Math.max(50, width_1 + dx),
                    height: Math.max(50, height_1 + dy),
                };
                setSize(__assign({}, _setSize));
                updateShadeData(pos, __assign({}, _setSize), revealedData);
            }
        };
        /** mouse move events */
        var handleMouseMove = function (e) { return handleMove(e.clientX, e.clientY); };
        /** touch move events */
        var handleTouchMove = function (e) {
            // Prevent default touch behavior to avoid page refresh/scrolling on iPad
            e.preventDefault();
            if (e.touches.length > 0) {
                handleMove(e.touches[0].clientX, e.touches[0].clientY);
            }
        };
        /** mouse up events */
        var endAction = function () {
            if (isDragging)
                endMultiDrag();
            setIsDragging(false);
            setIsResizing(false);
            setTriggerOnChange(true);
        };
        if ((isFocused && isDragging) || isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', endAction);
            window.addEventListener('touchmove', handleTouchMove, { passive: false });
            window.addEventListener('touchend', endAction);
            window.addEventListener('touchcancel', endAction);
        }
        return function () {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', endAction);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', endAction);
            window.removeEventListener('touchcancel', endAction);
        };
    }, [isDragging, isResizing, isFocused]);
    /** MULTI-SELECT FOLLOWER — move this shade when some OTHER selected item (of any
     * type: manipulative, text, annotation) is dragged. Mirrors the generic
     * inMultiSel pattern already used by ColoredTiles/LinearPiece. */
    useEffect(function () {
        var isMultiSelected = selectedTool.includes(localObj.selectionType) && selectedTool.length > 1;
        // eslint-disable-next-line no-console
        console.log('[Shades debug] follower effect (re)registering', {
            id: localObj.selectionType,
            selectedTool: selectedTool,
            isMultiSelected: isMultiSelected,
            note: isMultiSelected ? 'listeners attached' : 'listeners NOT attached — will never follow',
        });
        if (!isMultiSelected)
            return;
        var myStart = __assign({}, posRef.current);
        var followerOldData = null;
        var onStart = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === localObj.selectionType)
                return;
            myStart = __assign({}, posRef.current);
            followerOldData = {
                position: __assign({}, posRef.current),
                dimensions: __assign({}, sizeRef.current),
                revealed: revealedDataRef.current,
            };
            // eslint-disable-next-line no-console
            console.log('[Shades debug] follower onStart', { id: localObj.selectionType, sourceId: sourceId, myStart: myStart });
        };
        var onMove = function (_a) {
            var sourceId = _a.sourceId, deltaX = _a.deltaX, deltaY = _a.deltaY;
            if (sourceId === localObj.selectionType || isPositionLocked)
                return;
            var inMultiSel = selectedToolRef.current.includes(localObj.selectionType) &&
                selectedToolRef.current.length > 1;
            // eslint-disable-next-line no-console
            console.log('[Shades debug] follower onMove', {
                id: localObj.selectionType,
                sourceId: sourceId,
                deltaX: deltaX,
                deltaY: deltaY,
                inMultiSel: inMultiSel,
                selectedToolSnapshot: selectedToolRef.current,
            });
            if (!inMultiSel)
                return;
            var newPos = { x: myStart.x + deltaX, y: myStart.y + deltaY, z: posRef.current.z };
            setPos(newPos);
            updateShadeData(newPos, sizeRef.current, revealedDataRef.current);
        };
        var onEnd = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === localObj.selectionType)
                return;
            if (followerOldData) {
                var entry = {
                    type: 'shades',
                    id: String(shadeIndex),
                    propertyName: 'completeData',
                    old: JSON.stringify({ data: __assign({}, followerOldData) }),
                    new: JSON.stringify({
                        data: { position: __assign({}, posRef.current), dimensions: sizeRef.current, revealed: revealedDataRef.current },
                    }),
                };
                if (!registerMultiSelectSaveEntries(sourceId, [entry])) {
                    saveState(entry);
                }
                customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
            }
            followerOldData = null;
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        customEventHandler.addEventListener('multiSelectMoveEnd', onEnd);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
            customEventHandler.removeEventListener('multiSelectMoveEnd', onEnd);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTool, isPositionLocked]);
    /** KEYBOARD ACCESSIBILITY */
    var handleMoveKeyDown = function (e) {
        var _a;
        if (e.currentTarget !== e.target)
            return;
        // Context menu: Enter, Space, Shift+F10, ContextMenu key
        if (e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            e.stopPropagation();
            if (!selectedTool.includes(localObj.selectionType)) {
                setSelectedTool([localObj.selectionType]);
            }
            var rect = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect) {
                setContextMenu({
                    x: rect.right,
                    y: rect.top,
                    manipulativeId: localObj.selectionType,
                });
            }
            return;
        }
        if (isPositionLocked)
            return;
        var step = 2;
        var newPos = __assign({}, pos);
        switch (e.key) {
            case 'ArrowUp':
                e.preventDefault();
                newPos.y -= step;
                break;
            case 'ArrowDown':
                e.preventDefault();
                newPos.y += step;
                break;
            case 'ArrowLeft':
                e.preventDefault();
                newPos.x -= step;
                break;
            case 'ArrowRight':
                e.preventDefault();
                newPos.x += step;
                break;
            default:
                return;
        }
        setPos(newPos);
        updateShadeData(__assign({}, newPos), size, revealedData);
        onChange === null || onChange === void 0 ? void 0 : onChange(id, 'keyboardMove', {
            position: newPos,
            dimensions: size,
            revealed: revealedData,
        });
    };
    var handleResizeKeyDown = function (e) {
        if (e.currentTarget !== e.target || isPositionLocked)
            return;
        var step = 10;
        var newSize = __assign({}, size);
        switch (e.key) {
            case 'ArrowUp':
                e.preventDefault();
                newSize.height = Math.max(50, newSize.height - step);
                break;
            case 'ArrowDown':
                e.preventDefault();
                newSize.height += step;
                break;
            case 'ArrowLeft':
                e.preventDefault();
                newSize.width = Math.max(50, newSize.width - step);
                break;
            case 'ArrowRight':
                e.preventDefault();
                newSize.width += step;
                break;
            default:
                return;
        }
        // Update state once
        setSize(newSize);
        updateShadeData(pos, __assign({}, newSize), revealedData);
        // Trigger change immediately (like in keyboard move)
        onChange === null || onChange === void 0 ? void 0 : onChange(id, 'keyboardResize', {
            position: pos,
            dimensions: newSize,
            revealed: revealedData,
        });
    };
    /** IS FOCUSED */
    useEffect(function () {
        setIsFocused(selectedTool.includes(localObj.selectionType));
    }, [selectedTool]);
    /** BOTTOM PANEL EVENT */
    useEffect(function () {
        var onToolClick = function (id) {
            var splittedId = localObj.selectionType.split('_');
            var _shadeIndex = +splittedId[splittedId.length - 1];
            switch (id.id) {
                case 'visibility':
                    setShades(function (prev) {
                        var newState = __spreadArray([], prev, true);
                        newState[_shadeIndex] = __assign(__assign({}, newState[_shadeIndex]), { data: __assign(__assign({}, newState[_shadeIndex].data), { revealed: !newState[_shadeIndex].data.revealed }) });
                        return newState;
                    });
                    break;
                // case 'duplicate':
                //   setShades(prev => {
                //     let newState = [...prev];
                //     // const _prevData = newState[_shadeIndex].data;
                //     const _newData = {
                //       id: `id_${Date.now()}`,
                //       type: 'shade',
                //       data: {
                //         position: {
                //           x: pos.x + 10,
                //           y: pos.y + 10,
                //           z: pos.z,
                //         },
                //         dimensions: {
                //           ...size,
                //         },
                //         revealed: revealedData,
                //       },
                //     };
                //     newState.push(_newData);
                //     return newState;
                //   });
                //   break;
                // case 'delete':
                //   setShades(prev => prev.filter((_, i) => i !== _shadeIndex));
                //   break;
                default:
                    console.log(id.id);
                    break;
            }
        };
        if (isFocused)
            customEventHandler.addEventListener('bottomPanel', onToolClick);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
        };
    }, [isFocused]);
    return (_jsx("div", { id: "".concat(id, "_container"), ref: containerRef, "data-manipulative-id": localObj.selectionType, style: {
            position: 'absolute',
            left: "".concat(pos.x, "px"),
            top: "".concat(pos.y, "px"),
            // transform: `rotate(${rotation}deg)`,
            transformOrigin: 'left top',
            width: "".concat(size.width, "px"),
            height: "".concat(size.height, "px"),
            zIndex: 10,
            boxShadow: isFocused ? 'rgb(80, 99, 174) 0px 0px 20px' : 'none',
            borderRadius: '9px',
        }, children: _jsx("div", { id: id, role: "application", "aria-roledescription": "draggable and resizable panel", "aria-label": "Shade region", tabIndex: 0, className: "shadeContainer", style: {
                width: "".concat(size.width, "px"),
                height: "".concat(size.height, "px"),
                cursor: isFocused ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
            }, onMouseDown: handleMouseDownDrag, onTouchStart: handleTouchStartDrag, onKeyDown: handleMoveKeyDown, children: _jsxs("div", { id: "shade", className: "shades ".concat(revealedData ? 'revealed' : ''), children: [_jsx(ButtonComponent, { role: "application", imgSrc: (_c = btnConfig === null || btnConfig === void 0 ? void 0 : btnConfig.visibilty) === null || _c === void 0 ? void 0 : _c.imgSrc, defaultCSS: "visibiltyHandler", normalCSS: "visibiltyHandlers", ariaLabel: (_d = btnConfig === null || btnConfig === void 0 ? void 0 : btnConfig.visibilty) === null || _d === void 0 ? void 0 : _d.ariaLabel, imgWidth: 45, clickActive: true, 
                        // Drive the icon from revealedData (the source of truth) rather than the
                        // button's internal toggle state, which resets on remount after page
                        // navigation and left the icon showing eyeN while the shade stayed visible.
                        isActive: revealedData, controlActiveFromOutside: true, showToolTip: true, title: revealedData ? 'text.hide' : 'text.show', direction: "bottom", isParentRelative: true, clickHandler: function () {
                            var oldRevealed = revealedData;
                            setRevealedData(function (prev) { return !prev; });
                            // Save state for undo/redo
                            setShades(function (prev) {
                                var newState = __spreadArray([], prev, true);
                                newState[shadeIndex] = __assign(__assign({}, newState[shadeIndex]), { data: __assign(__assign({}, newState[shadeIndex].data), { revealed: !oldRevealed }) });
                                return newState;
                            });
                            saveState({
                                type: 'shades',
                                id: String(shadeIndex),
                                propertyName: 'completeData',
                                old: JSON.stringify({ data: __assign(__assign({}, props.data), { revealed: oldRevealed }) }),
                                new: JSON.stringify({ data: __assign(__assign({}, props.data), { revealed: !oldRevealed }) }),
                            });
                            customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                        } }, 'visibilityHandler'), _jsx(ButtonComponent, { role: "application", imgSrc: (_e = btnConfig === null || btnConfig === void 0 ? void 0 : btnConfig.resizeHandler) === null || _e === void 0 ? void 0 : _e.imgSrc, defaultCSS: "resizeHandle", normalCSS: "resizeHandles", ariaLabel: (_f = btnConfig === null || btnConfig === void 0 ? void 0 : btnConfig.resizeHandler) === null || _f === void 0 ? void 0 : _f.ariaLabel, imgWidth: 45, onMouseDown: handleMouseDownResize, onTouchStart: handleTouchStartResize, onKeyDown: handleResizeKeyDown }, 'resizeHandle')] }) }, id) }, "container"));
};
export default Shades;
